import {pick} from 'lodash-es';
import {useEffect, useReducer, useState} from 'react';

import {merchantService} from '@/services/merchants';
import type {BusinessFee} from '@/types';
import {getErrorMessage} from '@/utils/errorMessage';
import {showErrorMessage} from '@/utils/showErrorMessage';
import {showSuccessMessage} from '@/utils/showSuccessMessage';
import {BusinessFeeValidator} from '@/validators/BusinessFeeValidator';

import {FeeActionType} from './types';
import updateFeeReducer from './updateFeeReducer';

// Surface the most specific message the backend provides. A duplicate fee
// (AfriexErrorCode.DuplicateBusinessFee, HTTP 409) carries an admin-friendly
// reason on `details.friendlyMessage`; other errors fall back to the mapped
// error text, then the raw axios message.
const getSaveFeeErrorMessage = (e: unknown): string => {
  const serverMessage = getErrorMessage(e, '');
  return serverMessage
    ? `Error saving Fee: ${serverMessage}`
    : 'Error saving Fee';
};

const useFeeUpdate = (
  onClose: VoidFunction,
  initialData: Partial<BusinessFee> = {
    meta: {
      flags: {
        isDeactivated: false,
      },
    },
  },
  onSuccess?: () => void | Promise<void>,
) => {
  const [fee, dispatch] = useReducer(
    updateFeeReducer,
    initialData as BusinessFee,
  );
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    dispatch({
      type: FeeActionType.UPDATE_FEE,
      payload: initialData,
    });
  }, []);

  const handleSubmit = async (rawFee: BusinessFee) => {
    setIsLoading(true);
    try {
      const validFee = new BusinessFeeValidator(rawFee).value();
      if (rawFee.id) {
        const fieldsToInclude: (keyof BusinessFee)[] = [
          'name',
          'meta',
          'tags',
          'action',
          'amount',
        ];
        await merchantService.updateBusinessFee(
          rawFee.id,
          pick(validFee, fieldsToInclude),
        );
        showSuccessMessage(`Business fee updated successfully`);
      } else {
        await merchantService.createBusinessFee(validFee);
        showSuccessMessage(`Business fee saved successfully`);
      }

      onClose();

      if (onSuccess) {
        // Refresh the list in place and stay on the current page.
        await onSuccess();
      } else {
        // Fallback for callers that don't wire an in-place refresh.
        setTimeout(() => {
          window.location.reload();
        }, 1000);
      }
    } catch (e) {
      showErrorMessage(getSaveFeeErrorMessage(e));
    } finally {
      setIsLoading(false);
    }
  };

  return {
    dispatch,
    handleSubmit,
    isLoading,
    fee,
  };
};

export default useFeeUpdate;
